import { PropsWithChildren, useMemo } from 'react';
import { DefaultLayout } from '../../components/Layout/DefaultLayout';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../context/RoleContext';
import { useSite } from '../../context/SiteContext';
import { SiteData } from '../../types/sites';
import {
  FaClipboardList,
  FaDoorOpen,
  FaHospitalSymbol,
  FaKey,
  FaUserCircle,
  FaUserCog,
  FaUserShield,
} from 'react-icons/fa';
import {
  GET_STAFF_RELEASENOTE_ROLES,
  SUPERADMIN,
} from '../../configs/constants';
import { errorToast } from '../../utils/toast';
import { useCommonTranslation } from '../../hooks/i18n/useCommonTranslation';
import { useSangixUserActiveSiteRoles } from '../../hooks/user/useActiveSiteRoles';
import { TopBannersLayout } from '../common/layout/TopBannersLayout';
import { PRIVACY_POLICY_ROUTE } from '../../routes/AppRoutes';

export const LIST_MAX_SCROLL_HEIGHT = 735;

export const StaffLayout = ({ children }: PropsWithChildren<{}>) => {
  const { t } = useCommonTranslation();
  const { sangixUser, logout } = useAuth();
  const activeRoles = useSangixUserActiveSiteRoles();
  const { hasAnyOfRoles } = useRole();
  const { activeSite, setActiveSiteById, allSites } = useSite();

  const availableSites = useMemo(() => {
    if (hasAnyOfRoles(SUPERADMIN)) {
      return allSites
        ?.filter((site) => site.trust_id === activeSite.trust_id)
        .map((site) => ({
          site_id: site.id,
          site_short_name: site.short_name,
        }));
    }
    return activeRoles;
  }, [activeRoles, allSites, activeSite, hasAnyOfRoles]);

  const navbarDropdowns = useMemo(() => {
    return [
      {
        label: (activeSite as SiteData)?.short_name,
        labelAlt: t('sites'),
        icon: <FaHospitalSymbol />,
        accentColor: 'var(--blueprimary)',
        secondaryColor: 'var(--bluesecondary)',
        links:
          availableSites?.map((site) => ({
            label: site.site_short_name,
            onClickHandler: async () => {
              try {
                setActiveSiteById(site.site_id);
              } catch (err) {
                errorToast(err);
              }
            },
            icon: <FaHospitalSymbol />,
          })) || [],
      },
      {
        label: sangixUser
          ? sangixUser?.firstname + ' ' + sangixUser?.lastname
          : '',
        icon: <FaUserCircle />,
        accentColor: 'var(--greenprimary)',
        secondaryColor: 'var(--greentercial)',
        links: [
          ...(hasAnyOfRoles(SUPERADMIN)
            ? []
            : [
                {
                  label: t('my-profile'),
                  icon: <FaUserCog />,
                  onClickHandler: '/staff/profile',
                },
                {
                  label: t('password'),
                  icon: <FaKey />,
                  onClickHandler: '/staff/change-password',
                },
              ]),
          ...(hasAnyOfRoles(...GET_STAFF_RELEASENOTE_ROLES)
            ? [
                {
                  label: t('release-notes'),
                  icon: <FaClipboardList />,
                  onClickHandler: '/staff/release-notes',
                },
              ]
            : []),
          {
            label: t('privacy-policy'),
            icon: <FaUserShield />,
            onClickHandler: PRIVACY_POLICY_ROUTE,
          },
          {
            label: t('logout'),
            icon: <FaDoorOpen />,
            onClickHandler: () => {
              logout();
            },
          },
        ],
      },
    ];
  }, [
    hasAnyOfRoles,
    activeSite,
    availableSites,
    logout,
    setActiveSiteById,
    t,
    sangixUser,
  ]);
  return (
    <TopBannersLayout inPageWithSideMenu>
      <DefaultLayout navbar={{ dropdowns: navbarDropdowns }}>
        {children}
      </DefaultLayout>
    </TopBannersLayout>
  );
};
